




















































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';

import { saveAs } from 'file-saver';
import JsBarcode from 'jsbarcode';

import { STORE_KEY, store } from './_store';
import {
  PalletListRow,
  barcodeFormat,
  ean13FallbackWarning,
  extractShortName,
  groupFactsBySku,
} from '@/utils/palletList';
import {
  PalletListXlsxDoc,
  PalletListXlsxBlock,
  buildPalletListWorkbook,
  palletListXlsxFilename,
  formatFilledAtXlsx,
} from '@/utils/palletListXlsx';

interface PalletListBlock extends PalletListXlsxBlock {
  /** формат штрихкода тары из CRM (GS1-128 / EAN13 / пусто — авто) */
  barcode_format: string;
  rows: PalletListRow[];
}
// short_name в блоке — авто-извлечение из полного имени (extractShortName):
// поле в форме убрано, короткое название живёт только в имени листа/файла XLSX

@Component({
  computed: {
    ...mapState(STORE_KEY, [
      'loaded', 'loading', 'fatalError', 'documents', 'masterName'
    ])
  }
})
export default class ShiftPalletList extends Vue {
  // режимы открытия: factId — лист по одной партии; documentId — лист на всю смену
  @Prop({ type: String }) mode!: string;
  @Prop({ type: [Number, String] }) factId!: number | string;
  @Prop({ type: [Number, String] }) documentId!: number | string;

  documents!: any[];
  masterName!: string;

  blocks: PalletListBlock[] = [];
  fullName = '';
  filledAt = '';
  // на время сборки XLSX кнопка блокируется — повторные клики не накапливают работу
  isGenerating = false;

  public created() {
    // @ts-ignore
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }
  }

  public mounted() {
    this.$store.dispatch(`${STORE_KEY}/loadData`, {
      mode: this.mode,
      factId: this.factId,
      documentId: this.documentId,
    }).then(() => {
      this.fillForm();
    }).catch((error: any) => {
      console.error('[ShiftPalletList] loadData failed', error);
    });
  }

  public fillForm() {
    this.filledAt = formatFilledAtXlsx();
    // ФИО предзаполняется мастером смены (shift_workers с ролью is_manager);
    // поле редактируемое — при необходимости значение меняют перед печатью
    this.fullName = this.masterName || '';
    // Лист смены: факты группируются по SKU (продукт + тара + штрихкод) —
    // один блок на SKU, партии SKU заполняют несколько строк таблицы.
    // Одиночный лист (по факту) — одна группа из одного факта.
    this.blocks = groupFactsBySku(this.documents || []).map((group) => ({
      number: group.numbers[0] || '',
      numbers: group.numbers,
      short_name: group.short_name,
      product_name: group.product_name,
      trademark: group.trademark,
      container: group.container,
      barcode: group.barcode,
      barcode_format: group.barcode_format,
      rows: group.rows,
    }));
  }

  /** Заголовок карточки блока: SKU с несколькими партиями — «продукт — партии N, M» */
  public blockTitle(block: PalletListBlock, idx: number): string {
    const tail = this.blocks.length > 1 ? ` (${idx + 1} из ${this.blocks.length})` : '';
    const numbers = block.numbers && block.numbers.length ? block.numbers.filter(Boolean) : [block.number];
    if (numbers.length > 1) {
      return `${block.product_name || 'SKU'} — партии ${numbers.join(', ')}${tail}`;
    }
    return `Партия ${numbers[0] || '—'}${tail}`;
  }

  /** Текст предупреждения, когда EAN-13 не отрисовался (фолбэк CODE128):
   *  формат задан явно или выбран эвристикой, а значение некорректно.
   *  Текст общий с выгрузкой XLSX (utils/palletList). */
  public ean13FallbackWarning(block: PalletListBlock): string | null {
    return ean13FallbackWarning(block.barcode, block.barcode_format);
  }

  /** канвас превью штрихкода блока (v-for ref приходит массивом) */
  public barcodeCanvas(idx: number): HTMLCanvasElement | null {
    const ref: any = (this.$refs as any)['barcodeCanvas' + idx];
    const canvas = Array.isArray(ref) ? ref[0] : ref;
    return canvas instanceof HTMLCanvasElement ? canvas : null;
  }

  public redrawBarcodes() {
    this.$nextTick(() => {
      this.blocks.forEach((block, idx) => {
        const canvas = this.barcodeCanvas(idx);
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        if (!block.barcode) {
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
          return;
        }

        try {
          // CODE128 — компактный рендер, подпись значения в самой картинке
          const compact = barcodeFormat(block.barcode, block.barcode_format) === 'CODE128';
          JsBarcode(canvas, block.barcode.trim(), compact ? {
            format: 'CODE128',
            width: 1.6,
            height: 80,
            displayValue: true,
            fontSize: 16,
            margin: 4,
          } : {
            format: 'EAN13',
            width: 2,
            height: 60,
            fontSize: 14,
          });
        } catch (e) {
          // некорректное значение штрихкода — предупреждение уже показано
          console.error('[ShiftPalletList] barcode render failed', e);
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });
    });
  }

  @Watch('blocks', { deep: true })
  onBlocksChange() {
    this.redrawBarcodes();
  }

  /** data-URL штрихкода для XLSX и фактические размеры канваса
   *  (ExcelJS вставляет картинку по ext — бережём пропорции рендера) */
  public barcodeImage(
    barcode: string,
    format?: string | null,
  ): { url: string; width: number; height: number } | null {
    if (!barcode) return null;
    const canvas = document.createElement('canvas');
    const compact = barcodeFormat(barcode, format) === 'CODE128';
    try {
      // подпись значения печатается в самой картинке — отдельной строки
      // со значением в листе нет (иначе дублирование)
      JsBarcode(canvas, barcode.trim(), compact ? {
        format: 'CODE128',
        width: 1.6,
        height: 80,
        displayValue: true,
        fontSize: 16,
        margin: 4,
      } : {
        format: 'EAN13',
        width: 2.4,
        height: 80,
        fontSize: 18,
        margin: 4,
      });
      return { url: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
    } catch (e) {
      console.error('[ShiftPalletList] barcode render failed', e);
      return null;
    }
  }

  public async downloadXLSX() {
    if (this.isGenerating) return;
    if (this.blocks.length === 0) return;

    try {
      this.isGenerating = true;

      const doc: PalletListXlsxDoc = {
        full_name: this.fullName,
        filled_at: this.filledAt,
        blocks: this.blocks.map((block) => {
          const image = this.barcodeImage(block.barcode, block.barcode_format);
          return {
            ...block,
            // имя листа/файла строится по фактическому наименованию:
            // его могли отредактировать в форме после автоподстановки
            short_name: extractShortName(block.product_name),
            barcodeImage: image ? image.url : null,
            barcodeImageSize: image ? { width: image.width, height: image.height } : null,
            barcodeWarning: this.ean13FallbackWarning(block),
          };
        }),
      };

      const buffer = await buildPalletListWorkbook(doc);
      saveAs(new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }), palletListXlsxFilename(doc));
    } catch (e) {
      console.error('[ShiftPalletList] downloadXLSX failed', e);
      this.$buefy.snackbar.open({
        type: 'is-warning',
        message: 'Не удалось сформировать файл. Попробуйте позже.',
      });
    } finally {
      this.isGenerating = false;
    }
  }

  beforeDestroy() {
    this.$store.dispatch(`${STORE_KEY}/reset`);
  }
}
